import { Link } from 'react-router-dom'
import './SoftwarePage.css'
import './docs/Documentation.css'

const platforms = [
  {
    number: '01',
    category: 'Engineering simulation',
    name: 'AldapaNuCore',
    headline: 'Integrated engineering design, modeling, simulation, and analysis across physical domains.',
    description: 'Bring geometry, materials, coupled physics, and engineering decisions into one technical workflow.',
    tags: ['Geometry', 'Multiphysics', 'Nuclear'],
    image: '/media/software/nucore-interface.webp',
    alt: 'AldapaNuCore multiphysics simulation interface',
    path: '/software/aldapanucore',
  },
  {
    number: '02',
    category: 'Radiation detection',
    name: 'NuSpectra',
    headline: 'Radiation detection, spectroscopy, calibration, and scientific measurement software.',
    description: 'Move from live detector acquisition to calibrated spectra, peak analysis, and interpretable measurement results.',
    tags: ['Acquisition', 'Spectroscopy', 'Analytics'],
    image: '/media/software/nuspectra-interface.webp',
    alt: 'NuSpectra radiation spectroscopy and signal-analysis interface',
    path: '/software/nuspectra',
  },
]

const docs = [
  ['AldapaNuCore docs', '/docs/aldapanucore'],
  ['NuSpectra docs', '/docs/nuspectra'],
  ['NuMath docs', '/docs/numath'],
  ['AML docs', '/software/aml'],
  ['System Model guide', '/software/system-models'],
]

function SectionIntro({ number, label, title, copy }: { number: string; label: string; title: string; copy: string }) {
  return (
    <header className="sw-section-intro">
      <div className="sw-section-intro__index"><span>{number}</span><span>{label}</span></div>
      <h2>{title}</h2>
      <p>{copy}</p>
    </header>
  )
}

export function SoftwarePage() {
  return (
    <div className="software-page">
      <section className="sw-hero">
        <div className="sw-hero__grid" aria-hidden="true" />
        <div className="container sw-hero__inner">
          <div className="sw-hero__copy">
            <span className="sw-kicker">Aldapa Systems / Software</span>
            <h1>Engineering software<br />built around the physics.</h1>
            <p>Aldapa Systems develops simulation, measurement, scientific-computing, and engineering software for demanding technical workflows.</p>
            <div className="sw-hero__actions">
              <a className="button button--light" href="#software-platforms">View software <span>↓</span></a>
              <Link className="sw-text-link" to="/docs">Documentation <span>↗</span></Link>
            </div>
          </div>

          <div className="sw-collage" aria-label="Aldapa Systems software family preview">
            <div className="sw-collage__window sw-collage__window--nucore">
              <span>AldapaNuCore / coupled analysis</span>
              <img src="/media/software/nucore-interface.webp" alt="" width="1400" height="933" />
            </div>
            <div className="sw-collage__window sw-collage__window--spectra">
              <span>NuSpectra / live spectrum</span>
              <img src="/media/software/nuspectra-interface.webp" alt="" width="1400" height="788" />
            </div>
            <div className="sw-collage__code">
              <span>AML / calculation</span>
              <code>A = [4, 1; 2, 3]</code>
              <code>B = inverse(A)</code>
              <code>print(B)</code>
            </div>
            <div className="sw-collage__metric"><span>NuMath</span><strong>1.7e−08</strong><small>CONVERGED / 42 ITERATIONS</small></div>
          </div>

          <div className="sw-hero__footer"><span>Simulation</span><span>Measurement</span><span>Scientific computing</span><span>Engineering intelligence</span></div>
        </div>
      </section>

      <section className="sw-aee" id="engineering-environment"><div className="container"><div><small>Available in your browser</small><h2>Aldapa Engineering Environment</h2><p>Keep engineering notebooks, AML calculations, system diagrams, source tables and 3D models together in a project. Sign in to run calculations with the native C++ AML engine in the cloud.</p><div className="guide-links"><Link to="/app">Open AEE →</Link><Link to="/software/aml">Read the AML guide</Link></div></div><div className="sw-aee-cards"><Link to="/software/aml"><strong>AML language reference ↗</strong><span>Arithmetic, assignments, matrix operations, functions and typed behaviors.</span></Link><Link to="/software/system-models"><strong>System Model guide ↗</strong><span>Drag blocks, connect signals, import tables and edit custom block interfaces.</span></Link></div></div></section>

      <section className="sw-section sw-platforms" id="software-platforms">
        <div className="container">
          <SectionIntro number="01" label="Software platforms" title="Purpose-built technical software." copy="Two platforms focused on the work between engineering decisions and physical results." />
          <div className="sw-platform-grid">
            {platforms.map((product) => (
              <Link className="sw-platform-card" to={product.path} key={product.name}>
                <div className="sw-platform-card__top">
                  <div><span>Platform / {product.number}</span><strong>{product.category}</strong></div>
                  <span className="sw-status"><i /> In development</span>
                </div>
                <h3>{product.name}</h3>
                <p className="sw-platform-card__headline">{product.headline}</p>
                <div className="sw-platform-card__visual"><img src={product.image} alt={product.alt} loading="lazy" /></div>
                <div className="sw-platform-card__details">
                  <p>{product.description}</p>
                  <div className="sw-tags">{product.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                </div>
                <div className="sw-platform-card__cta"><span>Explore software</span><span>↗</span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-developer">
        <div className="container">
          <SectionIntro number="02" label="Developer tools" title="Software behind the software." copy="Numerical foundations and modeling tools for technical teams, automation, and the wider Aldapa Systems software ecosystem." />
          <div className="sw-developer-grid">
            <Link className="sw-tool-card" to="/software/numath">
              <div className="sw-tool-card__meta"><span>Library / 01</span><span>In development</span></div>
              <h3>NuMath</h3><p>Scientific numerical computing library.</p>
              <div className="sw-tool-card__visual sw-tool-card__visual--math" aria-hidden="true">
                <span className="sw-matrix">1.00&nbsp; 0.24&nbsp; −0.18<br />0.24&nbsp; 2.71&nbsp;&nbsp; 0.04<br />−0.18 0.04&nbsp;&nbsp; 3.16</span>
                <svg viewBox="0 0 500 120"><path d="M0 102 C70 100 78 88 130 87 S197 72 236 73 286 48 330 49 386 28 500 17" /><path className="sw-plot-faint" d="M0 108 C90 94 134 105 201 78 S316 75 374 43 438 39 500 28" /></svg>
                <span className="sw-convergence">Residual / convergence</span>
              </div>
              <div className="sw-tool-card__tags"><span>Linear algebra</span><span>Solvers</span><span>Optimization</span></div>
              <div className="sw-tool-card__cta">Explore NuMath <span>↗</span></div>
            </Link>

            <Link className="sw-tool-card" to="/software/aml">
              <div className="sw-tool-card__meta"><span>Language / 02</span><span>In development</span></div>
              <h3>AML</h3><p>Aldapa Model Language for engineering and scientific systems.</p>
              <div className="sw-tool-card__visual sw-tool-card__visual--code" aria-hidden="true">
                <div className="sw-code-lines"><span><i>model</i> ReactorLoop {'{'}</span><span>&nbsp;&nbsp;material = steel.316L</span><span>&nbsp;&nbsp;connect(core, exchanger)</span><span>&nbsp;&nbsp;<i>solve</i> multiphysics</span><span>{'}'}</span></div>
                <div className="sw-terminal"><span>$ aml run reactor.aml</span><span>Model validated&nbsp;&nbsp; ✓</span><span>18 components / 24 interfaces</span></div>
              </div>
              <div className="sw-tool-card__tags"><span>Modeling</span><span>Automation</span><span>Systems</span></div>
              <div className="sw-tool-card__cta">Explore AML <span>↗</span></div>
            </Link>
          </div>
        </div>
      </section>

      <section className="sw-section sw-ecosystem">
        <div className="container">
          <SectionIntro number="03" label="Software ecosystem" title="One ecosystem. Built to work together." copy="A shared foundation connects system definitions, numerical computation, simulation, and measurement." />
          <div className="sw-ecosystem-map">
            <svg viewBox="0 0 1000 360" preserveAspectRatio="none" aria-hidden="true">
              <path d="M500 47V136M500 136L240 250M500 136L500 250M500 136L760 250" />
              <circle cx="500" cy="136" r="4" />
            </svg>
            <div className="sw-node sw-node--aml"><small>Language</small><strong>AML</strong><span>Models + automation</span></div>
            <div className="sw-node sw-node--nucore"><small>Simulation</small><strong>AldapaNuCore</strong><span>Design + physics</span></div>
            <div className="sw-node sw-node--numath"><small>Computation</small><strong>NuMath</strong><span>Numerical methods</span></div>
            <div className="sw-node sw-node--spectra"><small>Measurement</small><strong>NuSpectra</strong><span>Acquisition + analysis</span></div>
            <span className="sw-ecosystem-map__future">Future libraries + APIs</span>
          </div>
        </div>
      </section>

      <section className="sw-docs">
        <div className="container sw-docs__inner">
          <div><span className="sw-kicker">04 / Documentation</span><h2>Technical references for<br />every Aldapa Systems software product.</h2><p>Guides, concepts, examples, and product references live in a dedicated documentation environment.</p></div>
          <nav aria-label="Software documentation">
            {docs.map(([label, path], index) => <Link to={path} key={label}><span>{String(index + 1).padStart(2, '0')}</span><strong>{label}</strong><i>↗</i></Link>)}
          </nav>
        </div>
      </section>
    </div>
  )
}
